import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

/* Raccourcis pour les secrets qu'on pose le plus souvent. */
const MODELES = [
  { titre: "Stripe", cles: ["STRIPE_SECRET_KEY", "STRIPE_PUBLISHABLE_KEY", "STRIPE_WEBHOOK_SECRET"] },
  { titre: "Envoi de mail (SMTP)", cles: ["SMTP_HOST", "SMTP_PORT", "SMTP_USER", "SMTP_PASSWORD"] },
  { titre: "SendGrid", cles: ["SENDGRID_API_KEY", "EMAIL_FROM", "EMAIL_ADMIN"] },
];

export default function MobileEnvPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem("odo_studio_token");

  const [donnees, setDonnees] = useState(null);
  const [saisies, setSaisies] = useState({});
  const [enregistre, setEnregistre] = useState(false);
  const [message, setMessage] = useState("");
  const [redemarrer, setRedemarrer] = useState(true);

  const charger = async () => {
    const r = await fetch(`${API_URL}/api/projects/${projectId}/env`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (r.ok) setDonnees(await r.json());
  };

  useEffect(() => {
    if (token) charger();
  }, [projectId, token]);

  const modifier = (cle, valeur) => setSaisies((s) => ({ ...s, [cle]: valeur }));

  const enregistrer = async () => {
    const vars = Object.entries(saisies)
      .filter(([, v]) => v && v.trim())
      .map(([key, value]) => ({ key, value: value.trim() }));
    if (!vars.length) return;
    setEnregistre(true);
    setMessage("");
    try {
      const r = await fetch(`${API_URL}/api/projects/${projectId}/env`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ vars, restart: redemarrer }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "échec");
      setSaisies({});
      setMessage(
        `${d.saved.length} variable(s) enregistrée(s)` +
          (d.restart ? (d.restart.code === 0 ? " · service redémarré" : " · redémarrage en échec") : "")
      );
      await charger();
    } catch (e) {
      setMessage(`⚠️ ${e.message}`);
    } finally {
      setEnregistre(false);
    }
  };

  const cles = new Set((donnees?.vars || []).map((v) => v.key));
  const aSaisir = Object.values(saisies).some((v) => v && v.trim());

  return (
    <div className="min-h-[100dvh] bg-[#faf9f7] text-[#1a1a1a]">
      <header
        className="sticky top-0 z-10 flex items-center gap-3 border-b border-black/5 bg-[#faf9f7]/90 px-4 backdrop-blur"
        style={{ paddingTop: "calc(max(env(safe-area-inset-top), 44px) + 8px)", paddingBottom: "10px" }}
      >
        <button
          onClick={() => navigate(`/m/projet/${projectId}`)}
          className="-ml-2 flex h-9 w-9 items-center justify-center rounded-full active:bg-black/5"
          style={{ touchAction: "manipulation" }}
          aria-label="Retour"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M15 18l-6-6 6-6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
        <div className="min-w-0 flex-1">
          <div className="text-[15px] font-semibold">Accès et secrets</div>
          <div className="truncate text-[12px] text-black/45">
            {donnees?.exists ? donnees.path : "aucun fichier .env"}
          </div>
        </div>
      </header>

      <div className="px-4 pt-4" style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 120px)" }}>
        <p className="mb-4 rounded-2xl bg-amber-50 px-4 py-3 text-[13px] leading-relaxed text-amber-900">
          Les valeurs ne sont jamais réaffichées. Une fois enregistrées, seules
          les quatre premières et dernières lettres restent visibles, pour vous
          permettre de reconnaître une clé.
        </p>

        {/* Variables déjà en place */}
        {donnees?.vars?.length > 0 && (
          <>
            <div className="mb-2 px-1 text-[12px] font-medium uppercase tracking-wide text-black/40">
              Déjà configurées
            </div>
            <div className="mb-6 overflow-hidden rounded-2xl border border-black/5 bg-white">
              {donnees.vars.map((v, i) => (
                <div
                  key={v.key}
                  className={`flex items-center gap-3 px-4 py-3 ${i ? "border-t border-black/5" : ""}`}
                >
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-mono text-[13px]">{v.key}</span>
                    <span className="block truncate font-mono text-[12px] text-black/35">
                      {v.preview || "vide"}
                    </span>
                  </span>
                  <span
                    className={`h-2 w-2 shrink-0 rounded-full ${
                      v.defined ? "bg-emerald-500" : "bg-amber-400"
                    }`}
                  />
                </div>
              ))}
            </div>
          </>
        )}

        {/* Saisie par famille */}
        {MODELES.map((m) => (
          <div key={m.titre} className="mb-5">
            <div className="mb-2 px-1 text-[12px] font-medium uppercase tracking-wide text-black/40">
              {m.titre}
            </div>
            <div className="overflow-hidden rounded-2xl border border-black/5 bg-white">
              {m.cles.map((cle, i) => (
                <div key={cle} className={i ? "border-t border-black/5" : ""}>
                  <label className="flex items-center gap-2 px-4 pt-2.5 text-[12px] text-black/45">
                    <span className="font-mono">{cle}</span>
                    {cles.has(cle) && (
                      <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] text-emerald-700">
                        définie
                      </span>
                    )}
                  </label>
                  <input
                    type="password"
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="off"
                    spellCheck={false}
                    value={saisies[cle] || ""}
                    onChange={(e) => modifier(cle, e.target.value)}
                    placeholder={cles.has(cle) ? "laisser vide pour ne pas changer" : "coller la valeur"}
                    className="w-full bg-transparent px-4 pb-2.5 font-mono outline-none placeholder:font-sans placeholder:text-black/25"
                    style={{ fontSize: "16px" }}
                  />
                </div>
              ))}
            </div>
          </div>
        ))}

        {message && (
          <div className="mb-3 rounded-2xl bg-black/[0.04] px-4 py-3 text-[14px]">{message}</div>
        )}
      </div>

      {/* Barre d'action */}
      <div
        className="fixed inset-x-0 bottom-0 border-t border-black/5 bg-[#faf9f7]/95 px-4 pt-3 backdrop-blur"
        style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 12px)" }}
      >
        <label className="mb-2 flex items-center gap-2 text-[13px] text-black/60">
          <input
            type="checkbox"
            checked={redemarrer}
            onChange={(e) => setRedemarrer(e.target.checked)}
            className="h-4 w-4"
          />
          Redémarrer le service après enregistrement
        </label>
        <button
          onClick={enregistrer}
          disabled={!aSaisir || enregistre}
          className="w-full rounded-2xl bg-[#1a1a1a] py-3.5 text-[16px] font-medium text-white transition-opacity disabled:opacity-25"
          style={{ touchAction: "manipulation" }}
        >
          {enregistre ? "Enregistrement…" : "Enregistrer"}
        </button>
      </div>
    </div>
  );
}
